import React, { useEffect, useState } from "react";
import { getEmployees, getLeaders, viewAllSalaries } from "../../http";
import { useHistory } from "react-router-dom";
import Loading from "../Loading";

const Salaries = () => {
  const history = useHistory();
  const [employees, setEmployees] = useState();
  const [employeeMap, setEmployeeMap] = useState();
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [salaries, setSalaries] = useState();

  useEffect(() => {
    let empObj = {};
    const fetchData = async () => {
      const res = await viewAllSalaries({});
      const { data } = res;
      setSalaries(data);
    };

    const fetchEmployees = async () => {
      const emps = await getEmployees();
      const leaders = await getLeaders();
      emps.data.forEach(
        (employee) => (empObj[employee.id] = [employee.name, employee.email])
      );
      leaders.data.forEach(
        (leader) => (empObj[leader.id] = [leader.name, leader.email])
      );
      setEmployeeMap(empObj);
      setEmployees([...emps.data, ...leaders.data]);
    };

    fetchData();
    fetchEmployees();
  }, []);

  const searchSalary = async () => {
    const obj = {};

    if (selectedEmployee) {
      obj["employeeID"] = selectedEmployee;
    }
    const res = await viewAllSalaries(obj);
    const { data } = res;
    setSalaries(data);
  };

  // Calculate total salary based on components (matching AssignSalary.jsx logic)
  const calculateTotalSalary = (salary) => {
    // Debug: log which employee we're calculating for
    console.log('Calculating salary for employee:', salary.employeeID);
    
    const basic = parseFloat(salary.basic) || 0;
    const hra = parseFloat(salary.hra) || 0;
    const medicalAllowance = parseFloat(salary.medicalAllowance) || 0;
    const conveyanceAllowance = parseFloat(salary.conveyanceAllowance) || 0;
    const bookAllowance = parseFloat(salary.bookAllowance) || 0;
    const specialPeriodicAllowance = parseFloat(salary.specialPeriodicAllowance) || 0;
    const gratuity = parseFloat(salary.gratuity) || 0;
    // Handle both field names: fuelAndMaintenance (from form) and fuelMaintenance (from API)
    const fuelAndMaintenance = parseFloat(salary.fuelAndMaintenance) || parseFloat(salary.fuelMaintenance) || 0;
    // Handle both field names: travelAssistance (from form) and TravelAssistance (from API)
    const travelAssistance = parseFloat(salary.travelAssistance) || parseFloat(salary.TravelAssistance) || 0;
    const pli = parseFloat(salary.pli) || 0;
    const others = parseFloat(salary.others) || 0;

    // Calculate employer PF (12% of basic) - included in total salary
    const employerPf = parseFloat(salary.employerPf) || (basic * 0.12);

    // Calculate total salary including all components
    const totalSalary =
      basic +
      hra +
      medicalAllowance +
      conveyanceAllowance +
      bookAllowance +
      specialPeriodicAllowance +
      pli +
      others +
      employerPf +
      gratuity +
      travelAssistance +
      fuelAndMaintenance;

    // Debug: log individual components to identify NaN source
    console.log('Salary components for employee', salary.employeeID, ':', {
      basic,
      hra,
      medicalAllowance,
      conveyanceAllowance,
      bookAllowance,
      specialPeriodicAllowance,
      pli,
      others,
      employerPf,
      gratuity,
      travelAssistance,
      fuelAndMaintenance,
      totalSalary,
      'isNaN(totalSalary)': isNaN(totalSalary)
    });

    return totalSalary;
  };

  // Calculate total deductions
  const calculateTotalDeduction = (salary) => {
    const basic = parseFloat(salary.basic) || 0;
    
    const employeePf = parseFloat(salary.employeePf) || (basic * 0.12);
    const employerPf = parseFloat(salary.employerPf) || (basic * 0.12);
    const tds = parseFloat(salary.tds) || 0;
    const otherDeduction = parseFloat(salary.otherDeduction) || 0;
    const loanAdvance = parseFloat(salary.loanAdvance) || 0;
    const gratuityDeduction = parseFloat(salary.gratuityDeduction) || 0;
    // Fixed room rent at 6000 for everyone
    const roomRent = 6000;
    // Handle both field names: insurance (from form) and insuranceEsi (from API)
    const insurance = parseFloat(salary.insurance) || parseFloat(salary.insuranceEsi) || 0;
    const pt = parseFloat(salary.pt) || 0;

    const totalDeduction =
      employeePf +
      employerPf +
      tds +
      otherDeduction +
      loanAdvance +
      gratuityDeduction +
      roomRent +
      insurance +
      pt;

    return totalDeduction;
  };

  // Calculate net payable salary
  const calculateNetPayableSalary = (salary) => {
    const totalSalary = calculateTotalSalary(salary);
    const totalDeduction = calculateTotalDeduction(salary);
    return totalSalary - totalDeduction;
  };

  return (
    <>
      {salaries ? (
        <div className="main-content">
          <section className="section">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h4>Salaries</h4>
              </div>
            </div>

            <div className="d-flex justify-content-center align-items-center w-100">
              <div className="form-group col-md-6">
                <label>Employee</label>
                <select
                  className="form-control select2"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}>
                  <option value="">Employees</option>
                  {employees?.map((employee) => (
                    <option key={employee._id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={searchSalary}
                className="btn btn-lg btn-primary col">
                Search
              </button>
            </div>
          </section>
          <div className="table-responsive">
            <table className="table table-striped table-md center-text">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Basic Salary</th>
                  <th>HRA</th>
                  <th>Medical Allowance</th>
                  <th>Conveyance Allowance</th>
                  <th>Book & Periodic Allowance</th>
                  <th>Special Allowance</th>
                  <th>Gratuity</th>
                  <th>Fuel & Maintenance</th>
                  <th>Travel Assistance</th>
                  <th>Employer PF</th>
                  <th>Total Salary</th>
                  <th>Employee PF</th>
                  <th>PLI</th>
                  <th>Others</th>
                  <th>TDS</th>
                  <th>PT</th>
                  <th>Gratuity Deduction</th>
                  <th>Other Deduction</th>
                  <th>Loan/Advance</th>
                  <th>Room Rent</th>
                  <th>Insurance</th>
                  <th>Total Deduction</th>
                  <th>Net Payable Salary</th>
                </tr>
              </thead>

              <tbody className="sidebar-wrapper">
                {salaries?.map((salary, idx) => (
                  <tr
                    key={salary._id || `${salary.employeeID}-${idx}`}
                    className="hover-effect"
                    onClick={() =>
                      history.push(`/salary/${salary.employeeID}`)
                    }>
                    <td>{idx + 1}</td>
                    <td>{employeeMap && employeeMap[salary.employeeID]?.[0]}</td>
                    <td>{employeeMap && employeeMap[salary.employeeID]?.[1]}</td>
                    <td>{salary.basic != null ? parseFloat(salary.basic).toFixed(2) : "0.00"}</td>
                    <td>{salary.hra != null ? parseFloat(salary.hra).toFixed(2) : "0.00"}</td>
                    <td>{salary.medicalAllowance != null ? parseFloat(salary.medicalAllowance).toFixed(2) : "0.00"}</td>
                    <td>{salary.conveyanceAllowance != null ? parseFloat(salary.conveyanceAllowance).toFixed(2) : "0.00"}</td>
                    <td>{salary.bookAllowance != null ? parseFloat(salary.bookAllowance).toFixed(2) : "0.00"}</td>
                    <td>{salary.specialPeriodicAllowance != null ? parseFloat(salary.specialPeriodicAllowance).toFixed(2) : "0.00"}</td>
                    <td>{salary.gratuity != null ? parseFloat(salary.gratuity).toFixed(2) : "0.00"}</td>
                    <td>{(salary.fuelAndMaintenance != null || salary.fuelMaintenance != null) ? parseFloat(salary.fuelAndMaintenance || salary.fuelMaintenance).toFixed(2) : "0.00"}</td>
                    <td>{(salary.travelAssistance != null || salary.TravelAssistance != null) ? parseFloat(salary.travelAssistance || salary.TravelAssistance).toFixed(2) : "0.00"}</td>
                    <td>{salary.employerPf != null ? parseFloat(salary.employerPf).toFixed(2) : "0.00"}</td>
                    <td>{!isNaN(calculateTotalSalary(salary)) ? calculateTotalSalary(salary).toFixed(2) : "0.00"}</td>
                    <td>{salary.employeePf != null ? parseFloat(salary.employeePf).toFixed(2) : "0.00"}</td>
                    <td>{salary.pli != null ? parseFloat(salary.pli).toFixed(2) : "0.00"}</td>
                    <td>{salary.others != null ? parseFloat(salary.others).toFixed(2) : "0.00"}</td>
                    <td>{salary.tds != null ? parseFloat(salary.tds).toFixed(2) : "0.00"}</td>
                    <td>{salary.pt != null ? parseFloat(salary.pt).toFixed(2) : "0.00"}</td>
                    <td>{salary.gratuityDeduction != null ? parseFloat(salary.gratuityDeduction).toFixed(2) : "0.00"}</td>
                    <td>{salary.otherDeduction != null ? parseFloat(salary.otherDeduction).toFixed(2) : "0.00"}</td>
                    <td>{salary.loanAdvance != null ? parseFloat(salary.loanAdvance).toFixed(2) : "0.00"}</td>
                    <td>6000.00</td>
                    <td>{(salary.insurance != null || salary.insuranceEsi != null) ? parseFloat(salary.insurance || salary.insuranceEsi).toFixed(2) : "0.00"}</td>
                    <td>{!isNaN(calculateTotalDeduction(salary)) ? calculateTotalDeduction(salary).toFixed(2) : "0.00"}</td>
                    <td>{!isNaN(calculateNetPayableSalary(salary)) ? calculateNetPayableSalary(salary).toFixed(2) : "0.00"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default Salaries;
